import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDateOnly } from '@safetyhub/shared';
import { Button, Card } from '@safetyhub/ui';
import { adminCompaniesApi } from '../lib/api';
import { apiErrorMessage } from '../lib/apiErrorMessage';
import { Badge } from '../components/Badge';
import { VerificationBadge, VERIFICATION_STATUS_MAP } from '../components/VerificationBadge';
import { BusinessStatusBadge } from '../components/BusinessStatusBadge';
import { CompanyReverifyConfirmModal } from '../components/CompanyReverifyConfirmModal';
import { InfoRow } from '../components/InfoRow';
import { useToastStore } from '../stores/toast';

const STALE_DAYS = 90;

function formatDate(value: string | null): string {
  if (!value) return '-';
  return new Date(value).toLocaleString('ko-KR');
}

export default function CompanyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToastStore();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'company', id],
    queryFn: () => adminCompaniesApi.detail(id!),
    enabled: !!id,
  });

  const reverifyMutation = useMutation({
    mutationFn: () => adminCompaniesApi.reverify(id!),
    onSuccess: (res) => {
      const when = res.data?.verifiedAt
        ? new Date(res.data.verifiedAt).toLocaleString('ko-KR')
        : '-';
      const status = res.data?.verificationStatus;
      const statusLabel = status ? VERIFICATION_STATUS_MAP[status]?.label ?? '' : '';
      const message = statusLabel
        ? `재검증 완료: ${statusLabel}. 검증일: ${when}`
        : `재검증 완료. 검증일: ${when}`;
      showToast(message, 'success');
      setConfirmOpen(false);
      queryClient.invalidateQueries({ queryKey: ['admin', 'company', id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] });
    },
    onError: (err) => {
      // ⛔ `code` 로 문구를 다시 파생하지 않는다 — 서버가 재검증 거부 사유를 자리별로 실어
      //    보내는데(`admin-companies.service.ts` 의 `reverify` 두 게이트) 그것을 버리고 코드
      //    기본 문구를 띄우던 자리다. 정본 헬퍼로 모았다 — 그 docblock 이 「호출부를 넓힐 때
      //    이 함수로 모을 것」이라 적고, axios 영문 raw 는 그 함수가 한 겹 막는다.
      showToast(apiErrorMessage(err, '재검증에 실패했습니다.'), 'error');
      setConfirmOpen(false);
    },
  });

  const company = data?.data?.company;
  const organization = data?.data?.organization ?? null;
  const sites = data?.data?.sites ?? [];

  const isStale = useMemo(() => {
    if (!company?.ntsVerifiedAt) return false;
    const verifiedAt = new Date(company.ntsVerifiedAt).getTime();
    const diff = Date.now() - verifiedAt;
    return diff > STALE_DAYS * 24 * 60 * 60 * 1000;
  }, [company?.ntsVerifiedAt]);

  const canReverify = !!company?.registrationNumber;

  const requestReverify = () => {
    if (!canReverify || reverifyMutation.isPending) return;
    // 이미 FULLY인 경우는 cascade 대상 없음 → 즉시 실행
    if (company?.verificationStatus === 'FULLY_VERIFIED') {
      reverifyMutation.mutate();
    } else {
      setConfirmOpen(true);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => navigate('/companies')}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
          >
            ← 목록으로
          </button>
        </div>
        <div className="bg-red-50 p-4 rounded text-red-600">
          사업장 정보를 불러오지 못했습니다.
        </div>
      </div>
    );
  }

  const fullAddress = [company.address, company.addressDetail].filter(Boolean).join(' ').trim();

  return (
    <div>
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/companies')}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
          >
            ← 목록으로
          </button>
          <h2 className="text-2xl font-bold text-gray-900">{company.name}</h2>
          <VerificationBadge status={company.verificationStatus} size="lg" />
          {isStale && <Badge color="yellow" label={`${STALE_DAYS}일 이상 경과`} />}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* 사업장 정보 */}
        <Card padding="xl" radius="lg" className="xl:col-span-2">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">사업장 정보</h3>
          <div>
            <InfoRow labelWidth="lg" label="상호" value={company.name} />
            <InfoRow labelWidth="lg" label="사업자등록번호" value={company.registrationNumber} />
            <InfoRow labelWidth="lg" label="대표자" value={company.ceoName} />
            <InfoRow labelWidth="lg" label="개업일" value={formatDateOnly(company.openingDate)} />
            <InfoRow labelWidth="lg" label="업종" value={company.industry} />
            <InfoRow labelWidth="lg" label="연락처" value={company.contact} />
            <InfoRow labelWidth="lg" label="이메일" value={company.email} />
            <InfoRow labelWidth="lg" label="우편번호" value={company.zonecode} />
            <InfoRow labelWidth="lg" label="주소" value={fullAddress || null} />
            <InfoRow labelWidth="lg" label="생성일" value={formatDate(company.createdAt)} />
            <InfoRow labelWidth="lg" label="수정일" value={formatDate(company.updatedAt)} />
          </div>
        </Card>

        {/* NTS 검증 메타 */}
        <Card padding="xl" radius="lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">NTS 검증 메타</h3>
          <div className="mb-4 p-4 bg-gray-50 rounded border border-gray-200">
            <div className="text-xs font-medium text-gray-500 mb-2">검증 상태</div>
            <VerificationBadge status={company.verificationStatus} size="lg" />
          </div>
          <div>
            <InfoRow labelWidth="lg" label="검증일" value={formatDate(company.ntsVerifiedAt)} />
            <InfoRow labelWidth="lg" label="사업 상태" value={<BusinessStatusBadge value={company.ntsBusinessStatus} />} />
            <InfoRow labelWidth="lg" label="국세청 정식 상호" value={company.ntsCanonicalName} />
            <InfoRow
              labelWidth="lg"
              label="경과 시간"
              value={isStale ? `${STALE_DAYS}일 이상 경과` : '최신'}
            />
          </div>

          <div className="mt-4 pt-4 border-t">
            <Button
              variant="primary"
              size="sm"
              onClick={requestReverify}
              disabled={!canReverify || reverifyMutation.isPending}
              className="w-full"
            >
              {reverifyMutation.isPending && (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
                  <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                </svg>
              )}
              사업자 정보 재검증
            </Button>
            {!canReverify && (
              <div className="mt-2 text-xs text-gray-500">
                사업자등록번호가 없는 사업장은 재검증할 수 없습니다.
              </div>
            )}
            {canReverify && company.verificationStatus !== 'FULLY_VERIFIED' && (
              <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 leading-relaxed">
                재검증이 성공하면 <strong>검증 완료</strong>로 승격되고 국세청
                정보(상호·상태)가 갱신됩니다. 다른 조직의 Company 레코드에는
                영향을 주지 않습니다.
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* 연결 조직 (이 Company 레코드와 연결된 조직) */}
      <Card padding="xl" radius="lg" className="mt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">연결 조직</h3>
        <p className="text-xs text-gray-500 mb-4">
          이 Company 레코드와 연결된 조직입니다. 한 Company 레코드는 최대 한 조직과 연결될 수 있습니다.
        </p>
        {organization ? (
          <button
            onClick={() => navigate(`/organizations`)}
            className="block w-full text-left border border-gray-200 rounded p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-900">{organization.name}</div>
                <div className="text-xs text-gray-500 mt-1">ID: {organization.id}</div>
              </div>
              <div className="text-sm text-gray-500">
                생성일: {formatDateOnly(organization.createdAt)}
              </div>
            </div>
          </button>
        ) : (
          <div className="text-sm text-gray-500">본사 연결 조직이 없습니다.</div>
        )}
      </Card>

      {/* 연결된 현장 */}
      <Card padding="none" radius="lg" className="mt-6 overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            연결된 현장 <span className="text-sm font-normal text-gray-500">({sites.length}개)</span>
          </h3>
        </div>
        {sites.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-gray-500">
            연결된 현장이 없습니다.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    현장명
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    조직
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    주소
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    생성일
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sites.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{s.name}</div>
                      <div className="text-xs text-gray-500">{s.id}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {s.organizationName || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {s.address ?? '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {formatDateOnly(s.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* 재검증 확인 다이얼로그 */}
      <CompanyReverifyConfirmModal
        isOpen={confirmOpen}
        isPending={reverifyMutation.isPending}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => reverifyMutation.mutate()}
      />
    </div>
  );
}
